import React, { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import classnames from 'classnames';
import KitIcon from './KitIcon';
import constants from '../common/theme/constants';
import ThemeColors from '../common/theme/colors';

const { fontSize, fontWeight, spacing } = constants;

export interface KitNotificationProps {
  children?: ReactNode;
  details?: string;
  fadeIn?: boolean;
  icon?: string;
  message?: string;
  mode?: string;
  size?: string;
  style?: object;
  type: string;
}

export enum KitNotificationLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
}

export enum KitNotificationIconType {
  INFO = 'information-outline',
  WARNING = 'warning',
  ERROR = 'warning',
  SUCCESS = 'check-bold',
}

const fadeInAnim = keyframes`
  0% {
      opacity: 0
  }
  100% {
    opacity: 1
  }
`;

const NotificationWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: ${spacing.absolute.s}px 0;
  padding: ${spacing.absolute.m}px;

  font-size: ${fontSize.text_14}rem;
  color: ${ThemeColors.gray_50};
  border: 1px solid ${ThemeColors.gray_20};
  background: ${ThemeColors.gray_10};
  transition: all 1s;

  &.info {
    background: ${ThemeColors.blue_20};
    border: 1px solid ${ThemeColors.blue_30};
  }

  &.warning {
    background: ${ThemeColors.orange_30};
    border: 1px solid ${ThemeColors.orange_55};
  }

  &.error {
    background: ${ThemeColors.red_10};
    border: 1px solid ${ThemeColors.red_50};
  }

  &.success {
    background: ${ThemeColors.green_10};
    border: 1px solid ${ThemeColors.green_50};
  }

  &.full-size {
    margin: 0px 0px;
  }

  &.small {
    padding: ${spacing.absolute.m}px;
    font-size: ${fontSize.text_12}rem;
  }

  &.transparent {
    background: none;
    border: 1px solid ${ThemeColors.gray_30};
  }

  &.fade-in {
    opacity: 0;
    animation: ${fadeInAnim} 1s;
    animation-delay: 0.3s;
    animation-fill-mode: forwards;
  }
`;

const IconWrapper = styled.div`
  width: 30px;
  display: inline-block;
  margin-right: ${spacing.absolute.xs}px;
`;

const Message = styled.div`
  // font-weight: ${fontWeight.bold};
`;

const Details = styled.div``;

const TextWrapper = styled.div`
  display: inline-block;
`;

const KitNotification: React.FC<KitNotificationProps> = (props) => {
  const { children, icon, fadeIn, message, details, style, size, type } = props;

  const containerSize = size ?? 'default';
  const { mode = 'default' } = props;

  const colorMap = {
    info: ThemeColors.blue_30,
    success: ThemeColors.green_50,
    error: ThemeColors.red_50,
    warning: ThemeColors.orange_55,
  };

  const iconFill = colorMap[type];

  const notificationIcon =
    icon ||
    {
      success: 'check-bold',
      info: 'information-outline',
      warning: 'warning',
      error: 'warning',
    }[type];

  return (
    <NotificationWrapper
      style={style ?? {}}
      className={classnames(type, {
        'full-size': size === 'full-size',
        'fade-in': fadeIn,
        [containerSize]: containerSize,
        [mode]: mode,
      })}
    >
      {type && (
        <IconWrapper>
          <KitIcon fill={iconFill} size={`${constants.iconSize.sm}rem`} icon={notificationIcon} />
        </IconWrapper>
      )}
      {children && children}
      {!children && message && (
        <TextWrapper>
          <Message dangerouslySetInnerHTML={{ __html: message }} />
          {details && <Details>{details}</Details>}
        </TextWrapper>
      )}
    </NotificationWrapper>
  );
};
export default KitNotification;
